// hooks/useIdleLogout.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Auto-logout after `timeoutMs` of no activity.
 * Shows a countdown during the last `warnOffsetMs`.
 */
export function useIdleLogout(
  logoutFn: () => void,
  timeoutMs = 30 * 60 * 1000,
  warnOffsetMs = 2 * 1000 // default: 2 seconds
) {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number>(timeoutMs);

  const endAtRef = useRef<number>(Date.now() + timeoutMs);
  const intervalRef = useRef<number | null>(null);
  const didLogoutRef = useRef(false);
  const logoutFnRef = useRef(logoutFn);

  // keep latest logoutFn
  useEffect(() => {
    logoutFnRef.current = logoutFn;
  }, [logoutFn]);

  // reset the deadline on user activity
  const markActivity = useCallback(() => {
    endAtRef.current = Date.now() + timeoutMs;
    setIsWarning(false);
  }, [timeoutMs]);

  useEffect(() => {
    // heartbeat every second
    intervalRef.current = window.setInterval(() => {
      const left = Math.max(0, endAtRef.current - Date.now());
      setRemainingMs(left);
      setIsWarning(left <= warnOffsetMs);

      if (left <= 0 && !didLogoutRef.current) {
        didLogoutRef.current = true;
        // stop ticking before calling logout
        if (intervalRef.current) window.clearInterval(intervalRef.current);
        intervalRef.current = null;
        logoutFnRef.current();
      }
    }, 1000);

    // add real user activity listeners (avoid scroll/visibilitychange noise)
    const events: (keyof WindowEventMap)[] = ["mousemove", "mousedown", "keydown", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));

    // start from "now"
    markActivity();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActivity));
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [markActivity, warnOffsetMs]);

  // MM:SS text if you prefer it
  const formattedRemaining = useMemo(() => {
    const totalSec = Math.ceil(remainingMs / 1000);
    const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
    const ss = String(totalSec % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [remainingMs]);

  const remainingSec = Math.ceil(remainingMs / 1000);

  return { isWarning, formattedRemaining, remainingSec };
}
